import React, { useRef, useState, useEffect } from "react";

// Format seconds as mm:ss or h:mm:ss if over 1h
function formatDisplay(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Parse mm:ss, m:ss, ss, h:mm:ss, etc.
function parseInput(str) {
  if (!str) return 0;
  const parts = str.split(":").map(Number);
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0] * 60; // interpret as minutes instead of seconds
  return 0;
}


export default function VisualTimerPie({
  timerSeconds = 1500,
  baseSeconds = 1500,
  onTimeChange,
  theme,
  running,
}) {
  const MAX_PIE_SECONDS = baseSeconds;
  const percent = Math.max(0, Math.min(1, timerSeconds / MAX_PIE_SECONDS));

  // Editable time state
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(formatDisplay(timerSeconds));
  const [wasFocused, setWasFocused] = useState(false);
  const inputRef = useRef();

  // Sync edit field when timer changes
  useEffect(() => {
    if (!editing) setEditValue(formatDisplay(timerSeconds));
  }, [timerSeconds, editing]);

  // Focus input on editing start
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // Pie path generator
  function getPiePath(cx, cy, r, percent) {
    if (percent <= 0) return "";
    if (percent >= 1) {
      // Draw a full counterclockwise circle using two arcs
      return `M${cx},${cy - r} A${r},${r} 0 1,0 ${cx},${cy + r} A${r},${r} 0 1,0 ${cx},${cy - r} Z`;
    }
    // Reverse direction: subtract percent instead of add
    const angle = -Math.PI / 2 - percent * 2 * Math.PI;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    const large = percent > 0.5 ? 1 : 0;
    // Change sweep flag from 1 to 0 for counterclockwise
    return `M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${large},0 ${x},${y} Z`;
  }

  // Drag handling (unchanged)
  const size = 270;
  const cx = size / 2;
  const cy = size / 2;
  const dialR = (size - 16) / 2;
  const bezelWidth = 8;
  const svgRef = useRef(null);
  const isDragging = useRef(false);

  const updateTimeFromCursor = e => {
    if (running) return;
    const rect = svgRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left - cx;
    let y = e.clientY - rect.top - cy;
    let angle = Math.atan2(y, x);
    if (angle < -Math.PI / 2) angle += 2 * Math.PI;
    angle += Math.PI / 2;
    // Reverse direction: subtract instead of use as-is
    const rawPercent = 1 - (angle / (2 * Math.PI));
    let rawSeconds = rawPercent * MAX_PIE_SECONDS;
    const snapIncrement = 30; // snap to 30s
    const anchored = Math.round(rawSeconds / snapIncrement) * snapIncrement;
    const newSec = Math.max(60, Math.min(anchored, MAX_PIE_SECONDS));
    if (onTimeChange) onTimeChange(newSec);
  };

  const handleMouseDown = e => {
    if (running) return;
    isDragging.current = true;
    updateTimeFromCursor(e);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  const handleMouseMove = e => {
    if (isDragging.current) updateTimeFromCursor(e);
  };
  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  // Styles
  const bg = theme?.colors?.background ?? "#fff";
  const pieFill = theme?.colors?.timerText ?? "#90CFFB";
  const baseFill = "#eee";
  const textCol = theme?.colors?.text ?? "#333";
  const font = theme?.fontFamily || "inherit";

  // Input handlers
  function handleInputChange(e) {
    setEditValue(e.target.value.replace(/[^\d:]/g, ""));
  }
  function handleInputBlurOrEnter(e) {
    if (e.type === "blur" || e.key === "Enter") {
      const newSecs = parseInput(editValue);
      setEditing(false);
      setWasFocused(false);
      if (newSecs > 0 && onTimeChange) onTimeChange(newSecs);
    }
    if (e.key === "Escape") {
      setEditing(false);
      setWasFocused(false);
      setEditValue(formatDisplay(timerSeconds));
    }
  }

  return (
    <div
      style={{
        width: size + bezelWidth * 2,
        height: size + bezelWidth * 2,
        border: `${bezelWidth}px solid ${bg}`,
        borderRadius: "50%",
        boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
        background: bg,
      }}
    >
      <svg
        ref={svgRef}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        onMouseDown={handleMouseDown}
        style={{
          display: "block",
          borderRadius: "50%",
          cursor: running ? "default" : "pointer",
        }}
      >
        <circle cx={cx} cy={cy} r={dialR} fill={baseFill} />
        <path d={getPiePath(cx, cy, dialR, percent)} fill={pieFill} />
        <circle
          cx={cx}
          cy={cy}
          r={size * 0.05}
          fill={bg}
          style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.15))" }}
        />
      </svg>

      {/* Editable time label */}
      <div
        style={{
          textAlign: "center",
          marginTop: 12,
          fontFamily: font,
        }}
      >
        {editing && !running ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleInputChange}
            onBlur={handleInputBlurOrEnter}
            onKeyDown={handleInputBlurOrEnter}
            maxLength={8}
            className="text-3xl font-bold px-2 py-1 rounded-lg text-center border border-zinc-300"
            style={{
              backgroundColor: bg,
              color: textCol,
              width: 110,
              fontFamily: font,
            }}
            onFocus={e => {
              if (!wasFocused) {
                setWasFocused(true);
                if (editValue !== "") setEditValue("");
              }
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 32,
              fontWeight: 600,
              color: textCol,
              cursor: running ? "default" : "pointer",
            }}
            onClick={() => { if (!running) setEditing(true); }}
            title={!running ? "Click to edit" : undefined}
          >
            {formatDisplay(timerSeconds)}
          </span>
        )}
      </div>
    </div>
  );
}
